import {
  AceActivityItemDto,
  AceActivityResponseDto,
} from './ace-activity.dto';
import {
  ActivityCategory,
  ActivityEntry,
  ActivityFeed,
  ActivityGroupView,
} from '../activity.model';
import { resolveAvatarUrl } from 'src/app/shared/utils/avatar-url';

// Classify an event into our category vocabulary. ACE's `category` field is
// unreliable on the member tab (it echoes the tab name, e.g. "member"), so we
// derive from the event `type` instead, falling back to the target entity type.
// Order matters: team-membership events carry a FUND_/TENANT_ prefix but are
// really team changes, so TEAM is checked before FUND. Unrecognised -> Other.
function classify(
  type: string | undefined,
  targetType: string | undefined
): ActivityCategory {
  const t = (type ?? '').toUpperCase();
  if (t.includes('TASK')) return ActivityCategory.Task;
  if (t.includes('TEAM')) return ActivityCategory.Team;
  if (t.includes('FUND')) return ActivityCategory.Fund;
  if (t.includes('REQUEST')) return ActivityCategory.Request;
  if (t.includes('CALENDAR') || t.includes('EVENT'))
    return ActivityCategory.Calendar;
  if (t.includes('DOCUMENT') || t.includes('DOC'))
    return ActivityCategory.Documents;
  if (t.includes('CHAT') || t.includes('MESSAGE')) return ActivityCategory.Chat;

  const target = (targetType ?? '').toLowerCase();
  if (target === 'fund') return ActivityCategory.Fund;
  if (target === 'task') return ActivityCategory.Task;
  return ActivityCategory.Other;
}

/**
 * Translate one ACE activity item into the app's ActivityEntry. The one place
 * that knows ACE's field names, its lowercase categories, and its nested
 * actor/target shapes.
 */
export function toActivityEntry(dto: AceActivityItemDto): ActivityEntry {
  return {
    id: dto.id,
    category: classify(dto.type, dto.target?.type),
    type: dto.type,
    title: dto.title,
    subtitle: dto.subtitle ?? undefined,
    status: dto.status ?? undefined,
    progress: dto.progress ?? undefined,
    occurredAt: dto.occurredAt ?? null,
    actorName: dto.actor?.name ?? undefined,
    actorAvatarUrl: resolveAvatarUrl(dto.actor?.avatarUrl),
    targetRoute: dto.target?.route ?? undefined,
  };
}

/**
 * Translate the ACE activity response payload (the envelope's `data`) into the
 * app's ActivityFeed, mapping both the flat item list and the day-bucketed
 * groups. Tolerates a missing/undefined payload (degrades to an empty feed).
 */
export function toActivityFeed(dto: AceActivityResponseDto | undefined): ActivityFeed {
  const groups: ActivityGroupView[] = (dto?.groups ?? []).map(g => ({
    key: g.key,
    title: g.title,
    entries: (g.items ?? []).map(toActivityEntry),
  }));
  return {
    groups,
    items: (dto?.items ?? []).map(toActivityEntry),
    nextCursor: dto?.nextCursor ?? null,
  };
}
